import { Stack, useTheme } from 'tamagui'
import Svg, { Circle } from 'react-native-svg'
import { useEffect, useRef } from 'react'
import { Animated, Easing } from 'react-native'

interface IProgressCircleProps {
  progress: number
  size?: number
  strokeWidth?: number
  backgroundColorToken?: string
  progressColorToken?: string
  showText?: boolean
  duration?: number
  delay?: number
}

export function ProgressCircle({
  progress,
  size = 40,
  strokeWidth = 4,
  backgroundColorToken = '$neutral-200',
  progressColorToken = '$info-300',
  showText = false,
  duration = 1000,
  delay = 300,
}: IProgressCircleProps) {
  const theme = useTheme()
  const normalizedProgress = Math.min(Math.max(progress, 0), 100)
  const prevProgress = useRef(normalizedProgress)
  const isFirstRender = useRef(true)
  const animatedValue = useRef(new Animated.Value(0)).current
  const textAnimatedValue = useRef(new Animated.Value(0)).current

  const backgroundColorValue = theme[backgroundColorToken]?.val || '#CFCFCF'
  const progressColorValue = theme[progressColorToken]?.val || '#3575D4'

  const radius = size / 2 - strokeWidth
  const center = size / 2
  const circumference = 2 * Math.PI * radius
  const textFontSize = size / 4

  useEffect(() => {
    if (isFirstRender.current || prevProgress.current !== normalizedProgress) {
      const animation = Animated.sequence([
        Animated.delay(isFirstRender.current ? delay : 0),
        Animated.parallel([
          Animated.timing(animatedValue, {
            toValue: normalizedProgress,
            duration,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
          }),
          Animated.timing(textAnimatedValue, {
            toValue: normalizedProgress,
            duration,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
          }),
        ]),
      ])

      animation.start(() => {
        isFirstRender.current = false
        prevProgress.current = normalizedProgress
      })

      return () => {
        animation.stop()
      }
    }
  }, [normalizedProgress, duration, delay, animatedValue, textAnimatedValue])

  const AnimatedCircle = Animated.createAnimatedComponent(Circle)

  const strokeDashoffset = animatedValue.interpolate({
    inputRange: [0, 100],
    outputRange: [circumference, 0],
    extrapolate: 'clamp',
  })

  const displayValue = textAnimatedValue.interpolate({
    inputRange: [0, 100],
    outputRange: ['0%', '100%'],
  })

  return (
    <Stack
      width={size}
      height={size}
      alignItems="center"
      justifyContent="center"
      testID="progress-circle"
    >
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          testID="progress-circle-background"
          cx={center}
          cy={center}
          r={radius}
          stroke={backgroundColorValue}
          strokeWidth={strokeWidth}
          opacity={0.4}
          fill="none"
        />

        <AnimatedCircle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColorValue}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90, ${center}, ${center})`}
        />
      </Svg>

      {showText ? (
        <Animated.Text
          testID="progress-circle-text"
          style={{
            position: 'absolute',
            fontSize: textFontSize,
            fontWeight: '700',
            color: progressColorValue,
          }}
        >
          {displayValue}
        </Animated.Text>
      ) : null}
    </Stack>
  )
}
